import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ResolveAppealForm } from '@/features/moderation/ResolveAppealForm';
import { useAuthStore } from '@/stores/auth';

export const Route = createFileRoute('/_authed/moderation/appeals/$id')({
  component: AppealDetailScreen,
});

function AppealDetailScreen() {
  const { id } = Route.useParams();
  const { t } = useTranslation('moderation');
  const currentUserId = useAuthStore((s) => s.userId);
  const [resolved, setResolved] = React.useState<'upheld' | 'overturned' | null>(null);

  return (
    <div className="space-y-6">
      <Link
        to="/moderation/queue"
        className="inline-flex items-center gap-1 text-sm text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
      >
        <ChevronLeft className="size-4 rtl:rotate-180" aria-hidden />
        {t('appeal.backToQueue')}
      </Link>
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('appeal.title')}</h1>
        <p className="font-mono text-sm text-[color:var(--color-fg-muted)]" dir="ltr">
          {id}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('appeal.formTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {resolved ? (
            <div className="space-y-3">
              <p className="text-sm text-[color:var(--color-fg)]">
                {t(`appeal.resolved.${resolved}`)}
              </p>
              {resolved === 'overturned' ? (
                <Button asChild variant="secondary">
                  <Link to="/moderation/queue">{t('appeal.followUpRestore')}</Link>
                </Button>
              ) : null}
            </div>
          ) : (
            <ResolveAppealForm
              appealId={id}
              currentUserId={currentUserId}
              onResolved={(s) => setResolved(s)}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
